import { data, Link, useLoaderData } from 'react-router'
import { Icon, type IconName } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/index.ts'

export async function loader({}: Route.LoaderArgs) {
	const categories = await prisma.category.findMany({
		where: {
			rentalListings: {
				some: {
					status: 'published',
				},
			},
		},
		select: {
			id: true,
			name: true,
			slug: true,
			icon: true,
		},
		orderBy: {
			name: 'asc',
		},
	})

	return data({ categories })
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Categories', url: '/categories' }
	},
	seo({
		loaderData,
		origin,
		canonical,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
		canonical: string
	}): SeoProps {
		const structuredData =
			loaderData.categories.length > 0
				? [
						buildItemListLd({
							name: 'Rental Categories',
							items: loaderData.categories.map((category) => ({
								name: category.name,
								url: `${origin}/rentals/category/${category.slug}`,
							})),
						}),
					]
				: undefined

		return {
			title: 'All Dallas - Fort Worth Rental Categories - RentDFW.com',
			description:
				'Browse all rental categories in the Dallas-Fort Worth area. Find the equipment and tools you need.',
			canonical,
			openGraph: {
				title: 'All Dallas - Fort Worth Rental Categories - RentDFW.com',
				description:
					'Browse all rental categories in the Dallas-Fort Worth area. Find the equipment and tools you need.',
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'All Dallas - Fort Worth Rental Categories - RentDFW.com',
				description:
					'Browse all rental categories in the Dallas-Fort Worth area. Find the equipment and tools you need.',
			},
			structuredData,
		}
	},
}

export default function CategoriesPage() {
	const { categories } = useLoaderData<typeof loader>()

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="mx-auto max-w-3xl text-center">
					<h1 className="text-4xl font-semibold text-balance">
						All Categories
					</h1>
					<p className="text-muted-foreground mt-2 mb-8 text-xl">
						Browse local rentals by category in the Dallas-Fort Worth metroplex.
					</p>
				</div>

				{categories.length === 0 ? (
					<div className="bg-muted rounded-lg py-16 text-center">
						<Icon
							name="file-text"
							className="text-muted-foreground mx-auto mb-4 size-12"
						/>
						<h3 className="mb-2 text-xl font-semibold">No Categories Yet</h3>
						<p className="text-muted-foreground">
							Categories will appear here once added.
						</p>
					</div>
				) : (
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{categories.map((category) => (
							<Link
								key={category.id}
								to={`/rentals/category/${category.slug}`}
								className="group"
							>
								<div className="bg-card group-hover:border-primary flex h-full flex-col items-center justify-between gap-2 rounded-lg border p-6 transition-all duration-300 group-hover:shadow-md">
									<div className="group-hover:bg-primary flex size-12 items-center justify-center rounded-full bg-white transition-all duration-300 group-hover:text-white dark:bg-black">
										<Icon
											name={(category.icon || 'scan-search') as IconName}
											className="size-7 opacity-60 transition-opacity duration-300 group-hover:opacity-100"
										/>
									</div>
									<h3 className="text-sm font-medium">{category.name}</h3>
								</div>
							</Link>
						))}
					</div>
				)}
			</div>
		</>
	)
}
